import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Sequence } from 'src/app/models/Sequence';

import { ConfirmationService } from '../../../shared/components/confirmation-modal/confirmation.service';
import { Campaign } from './../../../models/Campaign';
import { LeadList } from './../../../models/LeadList';
import { ToastService } from './../../../shared/components/toast/toast.service';
import { CampaignService } from './../../../shared/services/campaigns.service';
import { ListsService } from './../../../shared/services/lists.service';
import { ActiveCampaignService } from 'src/app/shared/services/active-campaign.service';

@Component({
  templateUrl: './campaign.component.html',
  styleUrls: ['./campaign.component.scss']
})
export class CampaignComponent implements OnInit, OnDestroy {

  title = 'Nova automação';

  active = true;
  lists: LeadList[];

  /** Só na edição: o formulário só aparece com a automação já dentro dele. */
  loadingCampaign = false;

  /** O select de lista espera a sua chamada; o resto do formulário não. */
  loadingLists = false;

  /** Gravação em curso — trava e gira o botão Salvar. */
  isSaving = false;

  campaignId: string;
  campaign: Campaign;
  sequences: Sequence[];
  listPostbacks: any[] = [];
  formCampaign: UntypedFormGroup;
  showAlert: boolean

  ac: boolean = false;
  acURLInvalid: boolean = false;
  sendWindow: boolean = false;

  @ViewChild('sendWindowInfoModal') sendWindowInfoModal: TemplateRef<any>;

  subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuild: UntypedFormBuilder,
    private campaignService: CampaignService,
    private listsService: ListsService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private ACService: ActiveCampaignService,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {
    this.formCampaign = this.formBuild.group({
      id: [''],
      name: ['', [Validators.required, Validators.minLength(5)]],
      lead_list_id: ['', Validators.required],
      acURL: ['', Validators.pattern('^$|^https:\\/\\/(?:[a-zA-Z0-9-]+\\.activehosted\\.com|[a-zA-Z0-9-]+\\.api-us\\d\\.com)$')],
      acKey: [''],
      active: [true],
      send_window_enabled: [false],
      send_window_start_hour: [23, [Validators.min(0), Validators.max(23)]],
      send_window_end_hour: [8, [Validators.min(0), Validators.max(23)]],
      send_window_grace_minutes: [15, [Validators.min(0), Validators.max(120)]]
    }, { validators: this.sendWindowDifferentHours });

    this.subscriptions.push(
      this.formCampaign.get('lead_list_id').valueChanges.subscribe((leadListId) => {
        const list = this.lists.find(l => l.id === leadListId);
        this.showAlert = list.integration && !list.integration.active;
      })
    );

    // O `loadingService.stop()` que existia aqui fechava o overlay global na mão
    // porque ele abria por cima desta tela. A rota agora cuida do próprio
    // carregamento, então não há overlay para fechar (CU-86akkubzz).
    this.loadingLists = true;
    this.subscriptions.push(
      this.listsService.listAll().subscribe({
        next: lists => {
          this.loadingLists = false;
          this.lists = lists;
          if (lists.length === 0) {
            this.toastService.error('Você deve cadastrar uma lista antes de criar uma automação!');
          }
          this.preselectListFromQuery();
        },
        error: () => { this.loadingLists = false; },
      })
    );

    this.subscriptions.push(
      this.activatedRoute.params.subscribe(params => {
        if (params.id) {
          this.campaignId = params.id;
          this.title = 'Alterar automação';
          this.loadCampaign();
        }
      })
    );

  }

  /**
   * Atalho vindo da config do agente (`/campaigns/new?lead_list_id=`): a lista já vem escolhida
   * para o cliente não ter que procurá-la de novo no combo.
   *
   * Só depois de `lists` carregar: o valueChanges de lead_list_id procura a lista no array para
   * decidir o alerta de integração inativa, e com o array vazio ele quebra.
   */
  private preselectListFromQuery(): void {
    const leadListId = this.activatedRoute.snapshot.queryParamMap.get('lead_list_id');

    if (!leadListId || this.campaignId) {
      return;
    }

    if (!this.lists?.some(l => l.id === leadListId)) {
      return;
    }

    this.formCampaign.patchValue({ lead_list_id: leadListId });
  }

  loadCampaign() {
    this.loadingCampaign = true;
    this.subscriptions.push(
      this.campaignService.findById(this.campaignId).subscribe(campaign => {
        this.loadingCampaign = false;
        this.formCampaign.patchValue(campaign);
        this.active = campaign.active;
        this.sendWindow = campaign.send_window_enabled || false;
        this.applySendWindowRequiredValidators(this.sendWindow);
        this.sequences = campaign.sequences;
        this.campaign = campaign;
        this.loadListPostbacks();
        this.observeListChanges();
        if (campaign.active_campaign) {
          this.ac = campaign.active_campaign.url ? true : false;
          this.formCampaign.patchValue({ acURL: campaign.active_campaign.url.replace('/api/3', ''), acKey: '--------------<YOUR_SECURE_KEY>-------------' })
        }
      })
    );
  }

  loadListPostbacks(): void {
    const listId = this.campaign?.lead_list?.id;
    if (!listId) {
      this.listPostbacks = [];
      return;
    }
    this.subscriptions.push(
      this.listsService.listPostbacks(listId, 20).subscribe(
        res => this.listPostbacks = res?.data ?? [],
        () => this.listPostbacks = []
      )
    );
  }

  observeListChanges() {
    this.subscriptions.push(
      this.formCampaign.get('lead_list_id').valueChanges.subscribe(id => {
        const campaign = this.campaign;
        campaign.lead_list = this.lists.find(l => l.id === id);
        this.campaign = campaign;

      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  toggleActive(): void {
    this.active = !this.active;
    this.formCampaign.patchValue({ active: this.active });
    if (this.campaignId) {
      this.save();
    }
  }

  saveCampaign(campaign): void {
    const successMessage = campaign.id
    ? 'Automação alterada com sucesso!'
    : 'Automação cadastrada com sucesso!';

    const errorMessage = campaign.id
      ? 'Erro ao alterar automação! Tente novamente mais tarde'
      : 'Erro ao cadastrar automação! Tente novamente mais tarde';

    this.isSaving = true;
    this.subscriptions.push(
      this.campaignService.save(campaign).subscribe(
        res => {
          this.isSaving = false;
          this.toastService.success(successMessage);
          if (!campaign.id) {
            this.router.navigate(['/campaigns/edit', res.id]);
          }

          // O PUT devolve a automação sem as relações do GET (lista, sequências...): sem a lista, o
          // subtítulo do cabeçalho e o botão Active Campaign da sequência sumiam depois de salvar. O
          // que o PUT não traz fica como estava; a lista é a do formulário.
          this.campaign = {
            ...this.campaign,
            ...res,
            lead_list: res?.lead_list ?? this.lists?.find(l => l.id === res?.lead_list_id) ?? this.campaign?.lead_list,
          };
        },
        err => {
          this.isSaving = false;
          this.toastService.error(errorMessage);
        }
      )
    );
  }

  save(): void {
    // "Enviar para" saiu da tela (09/10/2026, Bruno/Misael): automação vale só para os leads novos.
    // O campo segue no corpo para o backend anterior a esta versão, que exige send_to_all no PUT.
    const campaign = { ...this.formCampaign.getRawValue(), send_to_all: false } as Campaign;

    const apiURL = this.formCampaign.get('acURL').value;
    const apiKey = this.formCampaign.get('acKey').value;

    if (apiURL && apiKey && (this.formCampaign.get('acKey').dirty || this.formCampaign.get('acURL').dirty)) {
      this.ACService.activate(apiKey, apiURL).subscribe((activate) => {
        if (activate && activate.status === true) {
          this.toastService.success('Active Campaign validado com sucesso!');
          this.saveCampaign(campaign);
          this.ngOnInit();
          setTimeout(() => window.location.reload(), 3000);
        }else{
          this.formCampaign.patchValue({acKey: null, acURL: null});
          this.toastService.error('Dados do Active Campaign inválidos. Por favor, verifique se estão corretos e tente novamente.');
          return
        };
      })
    } else {
      if (!apiKey && !apiURL) {
        this.ac = false;
      }
      this.saveCampaign(campaign);
    }
  }

  resend(): void {
    const list = this.lists.find(l => l.id === this.formCampaign.get('lead_list_id')?.value);
    const message = `Esta automação será enviada novamente para todos os contatos da lista <b>${list.name}</b>. `
      + 'Deseja realmente reiniciar esta automação?';

    this.confirmationService.show(message, () => {
      this.subscriptions.push(
        this.campaignService.resend(this.campaignId).subscribe({
          next: () => this.toastService.success('Automação reenviada com sucesso!'),
          error: err => this.toastService.error(err.error?.message || 'Erro ao reenviar automação. Tente novamente mais tarde!')
        })
      );
    });
  }

  toggleSendWindow(): void {
    this.sendWindow = !this.sendWindow;
    this.formCampaign.patchValue({ send_window_enabled: this.sendWindow });
    this.applySendWindowRequiredValidators(this.sendWindow);
    if (this.sendWindow) {
      this.openSendWindowInfo();
    }
  }

  private applySendWindowRequiredValidators(required: boolean): void {
    const start = this.formCampaign.get('send_window_start_hour');
    const end = this.formCampaign.get('send_window_end_hour');
    const baseRange = [Validators.min(0), Validators.max(23)];
    start.setValidators(required ? [Validators.required, ...baseRange] : baseRange);
    end.setValidators(required ? [Validators.required, ...baseRange] : baseRange);
    start.updateValueAndValidity({ emitEvent: false });
    end.updateValueAndValidity({ emitEvent: false });
    this.formCampaign.updateValueAndValidity({ emitEvent: false });
  }

  private sendWindowDifferentHours(group: AbstractControl): ValidationErrors | null {
    const enabled = group.get('send_window_enabled')?.value;
    if (!enabled) {
      return null;
    }
    const start = group.get('send_window_start_hour')?.value;
    const end = group.get('send_window_end_hour')?.value;
    if (start !== null && end !== null && start !== '' && end !== '' && Number(start) === Number(end)) {
      return { sendWindowSameHours: true };
    }
    return null;
  }

  openSendWindowInfo(): void {
    if (this.sendWindowInfoModal) {
      this.modalService.open(this.sendWindowInfoModal, { centered: true, size: 'lg' });
    }
  }

  includeAC(): void {
    if (this.ac) {
      this.formCampaign.patchValue({ acURL: null, acKey: null })
      this.ac = false;
    }else {
      this.ac = true;
    }
  }
}
